import clsx from 'clsx';
import debounce from 'debounce-promise';
import moment from 'moment';
import printJS from 'print-js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from 'react-autocomplete';
import { ChevronLeft, File, Plus, Printer, X } from 'react-feather';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../components/confirm-modal';
import CustomerInfo from '../components/customer-info';
import CustomerModal from '../components/customer-modal';
import CustomersFinder from '../components/customers-finder';
import ExpirationModal from '../components/expiration-modal';
import NoteModal from '../components/note-modal';
import { InvoiceStatus } from '../enums/invoice-status';
import { InvoiceType } from '../enums/invoice-type';
import { Order } from '../enums/order';
import { OrganizationType } from '../enums/organization-type';
import { Role } from '../enums/role';
import { CreateInvoiceDto } from '../interfaces/create-invoice.dto';
import { Customer } from '../interfaces/customer';
import { Expiration } from '../interfaces/expiration';
import { GetPaginatedStockOrganization } from '../interfaces/get-paginated-stock-organization';
import { HttpExceptionDto } from '../interfaces/http-exception.dto';
import { Invoice } from '../interfaces/invoice';
import { InvoiceProduct } from '../interfaces/invoice-product';
import { InvoiceTotals } from '../interfaces/invoice-totals';
import { ManualInvoiceProduct } from '../interfaces/manual-invoice-product';
import { Organization } from '../interfaces/organization';
import { OrganizationClient } from '../interfaces/organization-client';
import { PaginatedDto } from '../interfaces/paginated.dto';
import { Product } from '../interfaces/product';
import { Stock } from '../interfaces/stock';
import { UpdateInvoiceDto } from '../interfaces/update-invoice.dto';
import { api } from '../services/api';
import { formatNumber, roundTwoDecimals } from '../services/helpers';
import myToastr from '../services/toastr';
import { useAppSelector } from '../store/hooks';
import { RootState } from '../store/store';
import { storeSelector } from '../store/store-slice';

interface ExtendedInvoiceProductDto {
  productId: number;
  units: number | null;
  amount: number | null;
  discountedAmount: number;
  discount: number | null;
  vatAmount: number;
  equivalenceSurcharge: number;
  subtotal: number;
  total: number;
  product: Product | null;
  hasErrorUnits: boolean;
  hasErrorAmount: boolean;
  hasErrorVatPercentage: boolean;
}

interface ExtendedManualInvoiceProductDto {
  name: string;
  units: number | null;
  amount: number | null;
  discountedAmount: number;
  discount: number | null;
  vatAmount: number;
  equivalenceSurcharge: number;
  subtotal: number;
  total: number;
  hasErrorUnits: boolean;
  hasErrorAmount: boolean;
  hasErrorVatPercentage: boolean;
}

interface NewInvoiceProductDto {
  productId: number;
  name: string;
  units: number | null;
  amount: number | null;
  discountedAmount: number;
  discount: number | null;
  vatAmount: number;
  equivalenceSurcharge: number;
  subtotal: number;
  total: number;
  product: Product | null;
  hasErrorUnits: boolean;
  hasErrorAmount: boolean;
  hasErrorVatPercentage: boolean;
}

interface ExtendedInvoiceDto {
  observations: string | null;
  date: string;
  invoiceType: InvoiceType;
  invoiceStatus: InvoiceStatus;
  storeId: number;
  customerId: number | null;
  customer: Customer | null;
  customerName: string;
  invoiceProducts: ExtendedInvoiceProductDto[];
  manualInvoiceProducts: ExtendedManualInvoiceProductDto[];
  notes: string | null;
  organizationClientId: number | null;
}

const debouncedStock = debounce(async (organizationId: number, query: string) => {
  try {
    const getPaginatedStockOrganization: GetPaginatedStockOrganization = {
      orderField: 'product.name',
      order: Order.Asc,
      limit: 50,
      page: 1,
      query,
      organizationId,
      storeId: -1,
      brandId: -1,
      categoryId: -1,
    };
    const paginatedDto: PaginatedDto<Stock> = await api.getStockOfOrganization(getPaginatedStockOrganization);
    return paginatedDto.results;
  } catch (e) {
    console.error(e);
    return [];
  }
}, 1000);

const InvoiceView = () => {
  const user = useAppSelector((state) => state.auth.user!);
  const customerSelectRef = useRef<any>(null);
  const organization: Organization = useAppSelector((state: RootState) => state.auth.organization!);
  const organizationType = useAppSelector((state: RootState) => state.auth.organizationType);
  const [organizationClients, setOrganizationClients] = useState<OrganizationClient[]>([]);
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedStoreId } = useSelector(storeSelector);
  const [stock, setStock] = useState<Stock[]>([]);
  const [query, setQuery] = useState<string>('');
  const [requesting, setRequesting] = useState<boolean>(false);
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const [extendedInvoiceDto, setExtendedInvoiceDto] = useState<ExtendedInvoiceDto>({
    observations: '',
    date: moment().format('YYYY-MM-DD'),
    invoiceType: (location.state as any)?.invoiceType ? (location.state as any).invoiceType : InvoiceType.Normal,
    invoiceStatus: InvoiceStatus.Pending,
    storeId: selectedStoreId,
    customerId: -1,
    customer: null,
    customerName: '',
    invoiceProducts: [],
    manualInvoiceProducts: [],
    notes: '',
    organizationClientId: -1,
  });
  const [newInvoiceProductDto, setNewInvoiceProductDto] = useState<NewInvoiceProductDto>({
    productId: -1,
    name: '',
    units: 1,
    amount: null,
    discountedAmount: 0,
    discount: null,
    vatAmount: 0,
    equivalenceSurcharge: 0,
    subtotal: 0,
    total: 0,
    product: null,
    hasErrorUnits: false,
    hasErrorAmount: false,
    hasErrorVatPercentage: false,
  });
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [requestingPdf, setRequestingPdf] = useState<boolean>(false);
  const [requestingXml, setRequestingXml] = useState<boolean>(false);
  const [showDeleteInvoiceConfirmModal, setShowDeleteInvoiceConfirmModal] = useState<boolean>(false);
  const [showDeleteExpirationConfirmModal, setShowDeleteExpirationConfirmModal] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState<boolean>(false);
  const organizationClient = useMemo(() => organizationClients.find((oc) => oc.id === extendedInvoiceDto.organizationClientId), [extendedInvoiceDto, organizationClients]);
  // Las organizaciones minoristas no usan recargo de equivalencia en las facturas
  const EQUIVALENCE_SURCHARGE = organizationClient?.equivalenceSurcharge ? parseFloat(process.env.REACT_APP_EQUIVALENCE_SURCHARGE!) : 0;
  const invoiceTotals: InvoiceTotals = useMemo(() => {
    const it: InvoiceTotals = {
      units: 0,
      taxBase: 0,
      equivalenceSurcharge: 0,
      vat: 0,
      total: 0,
    };
    extendedInvoiceDto.invoiceProducts.forEach((extendedInvoiceProductDto: ExtendedInvoiceProductDto) => {
      const units = extendedInvoiceProductDto.units !== null ? extendedInvoiceProductDto.units! : 0;
      const amount = extendedInvoiceProductDto.amount !== null ? extendedInvoiceProductDto.amount! : 0;
      const discountPercentage = extendedInvoiceProductDto.discount !== null ? extendedInvoiceProductDto.discount! : 0;
      const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
      const taxBase = amount * units * (1 - discountPercentage / 100);
      const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
      const vatAmount = (taxBase * vatPercentage) / 100;
      it.units += units;
      it.taxBase += taxBase;
      it.equivalenceSurcharge += equivalenceSurchargeAmount;
      it.vat += vatAmount;
      it.total += taxBase + equivalenceSurchargeAmount + vatAmount;
    });
    extendedInvoiceDto.manualInvoiceProducts.forEach((extendedManualInvoiceProductDto: ExtendedManualInvoiceProductDto) => {
      const units = extendedManualInvoiceProductDto.units !== null ? extendedManualInvoiceProductDto.units! : 0;
      const amount = extendedManualInvoiceProductDto.amount !== null ? extendedManualInvoiceProductDto.amount! : 0;
      const discountPercentage = extendedManualInvoiceProductDto.discount !== null ? extendedManualInvoiceProductDto.discount! : 0;
      const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
      const taxBase = amount * units * (1 - discountPercentage / 100);
      const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
      const vatAmount = (taxBase * vatPercentage) / 100;
      it.units += units;
      it.taxBase += taxBase;
      it.equivalenceSurcharge += equivalenceSurchargeAmount;
      it.vat += vatAmount;
      it.total += taxBase + equivalenceSurchargeAmount + vatAmount;
    });
    return {
      units: it.units,
      taxBase: roundTwoDecimals(it.taxBase),
      equivalenceSurcharge: roundTwoDecimals(it.equivalenceSurcharge),
      vat: roundTwoDecimals(it.vat),
      total: roundTwoDecimals(it.total),
    };
  }, [extendedInvoiceDto, EQUIVALENCE_SURCHARGE]);
  const isAddProductDisabled = useMemo(() => {
    if (newInvoiceProductDto.productId === -1 && newInvoiceProductDto.name === '') {
      return true;
    }
    if (newInvoiceProductDto.units === null || newInvoiceProductDto.units <= 0) {
      return true;
    }
    if (extendedInvoiceDto.invoiceType === InvoiceType.Normal) {
      if (newInvoiceProductDto.amount === null || newInvoiceProductDto.amount <= 0) {
        return true;
      }
    } else {
      if (newInvoiceProductDto.amount === null || newInvoiceProductDto.amount >= 0) {
        return true;
      }
    }
    return false;
  }, [newInvoiceProductDto, extendedInvoiceDto]);
  const disableButton: boolean = useMemo(() => {
    if (extendedInvoiceDto.invoiceProducts.length === 0 && extendedInvoiceDto.manualInvoiceProducts.length === 0) {
      return true;
    }
    return false;
  }, [extendedInvoiceDto]);
  const showNewCorrectiveInvoice: boolean = useMemo(() => {
    if (!invoice || invoice.invoiceStatus === InvoiceStatus.Pending || invoice.invoiceType === InvoiceType.Rectification) {
      return false;
    }
    if (![Role.SuperAdmin, Role.Admin, Role.Manager].includes(user.role)) {
      return false;
    }
    if (invoice.rectifyingInvoices.length === 0) {
      return true;
    }
    const isThereSomePendingToConfirm = invoice.rectifyingInvoices.some((ri) => ri.invoiceStatus === InvoiceStatus.Pending);
    if (isThereSomePendingToConfirm) {
      return false;
    }
    for (const invoiceProduct of invoice.invoiceProducts) {
      const returnedUnits = invoice.rectifyingInvoices.reduce((sum, ri) => {
        const rip = ri.invoiceProducts.find((p) => p.productId === invoiceProduct.productId);
        if (rip) {
          return sum + Math.abs(rip.units);
        }
        return sum;
      }, 0);
      if (returnedUnits < invoiceProduct.units) {
        return true;
      }
    }
    for (const manualInvoiceProduct of invoice.manualInvoiceProducts) {
      const returnedUnits = invoice.rectifyingInvoices.reduce((sum, ri) => {
        const rip = ri.manualInvoiceProducts.find((p) => p.name === manualInvoiceProduct.name);
        if (rip) {
          return sum + Math.abs(rip.units);
        }
        return sum;
      }, 0);
      if (returnedUnits < manualInvoiceProduct.units) {
        return true;
      }
    }
    return false;
  }, [user, invoice]);

  const getInvoice = async () => {
    try {
      const id: number = parseInt(params.id!, 10);
      const i: Invoice = await api.getInvoice(id);
      if (i.invoiceType === InvoiceType.Rectification && i.invoiceStatus === InvoiceStatus.Pending) {
        navigate(`/factura/${i.parentInvoiceId}/rectificativa/${i.id}`);
        return;
      }
      setInvoice(i);
      setExtendedInvoiceDto({
        observations: i.observations,
        date: moment(i.date).format('YYYY-MM-DD'),
        invoiceType: i.invoiceType,
        invoiceStatus: i.invoiceStatus,
        storeId: i.storeId,
        customerId: i.customerId,
        customer: i.customer,
        customerName: i.customer?.name ? i.customer.name : '',
        invoiceProducts: i.invoiceProducts.map((invoiceProduct: InvoiceProduct) => {
          const discountedAmount = invoiceProduct.amount * (1 - (invoiceProduct.discount || 0) / 100);
          const taxBase = invoiceProduct.amount * invoiceProduct.units * (1 - (invoiceProduct.discount || 0) / 100);
          const vatAmount = (taxBase * parseFloat(process.env.REACT_APP_IVA!)) / 100;
          return {
            productId: invoiceProduct.productId,
            units: invoiceProduct.units,
            amount: invoiceProduct.amount,
            discountedAmount: roundTwoDecimals(discountedAmount),
            vatAmount: roundTwoDecimals(vatAmount),
            equivalenceSurcharge: roundTwoDecimals(invoiceProduct.equivalenceSurcharge),
            discount: invoiceProduct.discount,
            subtotal: roundTwoDecimals(taxBase),
            total: invoiceProduct.total,
            product: invoiceProduct.product,
            hasErrorUnits: false,
            hasErrorAmount: false,
            hasErrorVatPercentage: false,
          };
        }),
        manualInvoiceProducts: i.manualInvoiceProducts.map((manualInvoiceProduct: ManualInvoiceProduct) => {
          const discountedAmount = manualInvoiceProduct.amount * (1 - (manualInvoiceProduct.discount || 0) / 100);
          const taxBase = manualInvoiceProduct.amount * manualInvoiceProduct.units * (1 - (manualInvoiceProduct.discount || 0) / 100);
          const vatAmount = (taxBase * parseFloat(process.env.REACT_APP_IVA!)) / 100;
          return {
            name: manualInvoiceProduct.name,
            units: manualInvoiceProduct.units,
            amount: manualInvoiceProduct.amount,
            discountedAmount: roundTwoDecimals(discountedAmount),
            vatAmount: roundTwoDecimals(vatAmount),
            equivalenceSurcharge: roundTwoDecimals(manualInvoiceProduct.equivalenceSurcharge),
            discount: manualInvoiceProduct.discount,
            subtotal: roundTwoDecimals(taxBase),
            total: manualInvoiceProduct.total,
            hasErrorUnits: false,
            hasErrorAmount: false,
            hasErrorVatPercentage: false,
          };
        }),
        notes: i.notes,
        organizationClientId: i.organizationClientId,
      });
    } catch (e: any) {
      const httpExceptionDto: HttpExceptionDto = e.response.data;
      myToastr.error(httpExceptionDto.message);
      navigate('/facturas');
    }
  };

  useEffect(() => {
    if (params.id) {
      getInvoice();
    }
  }, [params?.id]);

  useEffect(() => {
    if (invoice !== null) {
      return;
    }
    if (selectedStoreId) {
      setExtendedInvoiceDto({ ...extendedInvoiceDto, storeId: selectedStoreId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStoreId]);

  useEffect(() => {
    if (!query) {
      setStock([]);
      return;
    }
    getStock(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (organizationType === OrganizationType.B2B) {
      getOrganizationClients();
    }
  }, [organizationType]);

  const getOrganizationClients = async () => {
    try {
      const ocs: OrganizationClient[] = await api.getOrganizationClients(organization.id);
      setOrganizationClients(ocs.filter((oc) => oc.organizationId === organization.id));
    } catch (e) {
      console.error(e);
    }
  };

  const getStock = async (query: string) => {
    debouncedStock(organization.id, query).then((response: Stock[]) => {
      const map: Map<number, Stock> = new Map<number, Stock>();
      response.forEach((stock: Stock) => {
        if (map.has(stock.product.id)) {
          // Prevalece el stock de la tienda
          if (stock.store.id === selectedStoreId) {
            map.set(stock.product.id, stock);
          }
        } else {
          map.set(stock.product.id, stock);
        }
      });
      const distinctStock: Stock[] = Array.from(map.values()).sort((a: Stock, b: Stock) => a.product.name.localeCompare(b.product.name));
      setStock(distinctStock);
    });
  };

  // PRODUCTOS BD

  const removeProduct = (index: number) => {
    const invoiceProducts = [...extendedInvoiceDto.invoiceProducts];
    invoiceProducts.splice(index, 1);
    setExtendedInvoiceDto({ ...extendedInvoiceDto, invoiceProducts });
  };

  const onChangeUnits = (index: number, newUnits: number | null) => {
    const invoiceProducts = [...extendedInvoiceDto.invoiceProducts];
    if (newUnits === null) {
      invoiceProducts[index].units = null;
      invoiceProducts[index].hasErrorUnits = true;
    } else {
      invoiceProducts[index].units = newUnits;
      invoiceProducts[index].hasErrorUnits = false;
    }
    const amount = invoiceProducts[index].amount !== null ? invoiceProducts[index].amount! : 0;
    const discount = invoiceProducts[index].discount !== null ? invoiceProducts[index].discount! : 0;
    const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
    const taxBase = amount * (invoiceProducts[index].units || 0) * (1 - discount / 100);
    const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
    const vatAmount = (taxBase * vatPercentage) / 100;
    invoiceProducts[index].subtotal = roundTwoDecimals(taxBase);
    invoiceProducts[index].vatAmount = roundTwoDecimals(vatAmount);
    invoiceProducts[index].total = roundTwoDecimals(taxBase + equivalenceSurchargeAmount + vatAmount);
    setExtendedInvoiceDto({ ...extendedInvoiceDto, invoiceProducts });
  };

  const onChangeAmount = (index: number, newAmount: number | null) => {
    const invoiceProducts = [...extendedInvoiceDto.invoiceProducts];
    if (newAmount === null) {
      invoiceProducts[index].amount = null;
      invoiceProducts[index].hasErrorAmount = true;
      invoiceProducts[index].total = 0;
    } else {
      invoiceProducts[index].amount = newAmount;
      if (extendedInvoiceDto.invoiceType === InvoiceType.Normal) {
        invoiceProducts[index].hasErrorAmount = newAmount <= 0;
      } else {
        invoiceProducts[index].hasErrorAmount = newAmount >= 0;
      }
    }
    const units = invoiceProducts[index].units !== null ? invoiceProducts[index].units! : 0;
    const discount = invoiceProducts[index].discount !== null ? invoiceProducts[index].discount! : 0;
    const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
    const taxBase = (invoiceProducts[index].amount || 0) * units * (1 - discount / 100);
    const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
    const vatAmount = (taxBase * vatPercentage) / 100;
    invoiceProducts[index].subtotal = roundTwoDecimals(taxBase);
    invoiceProducts[index].vatAmount = roundTwoDecimals(vatAmount);
    invoiceProducts[index].total = roundTwoDecimals(taxBase + equivalenceSurchargeAmount + vatAmount);
    setExtendedInvoiceDto({ ...extendedInvoiceDto, invoiceProducts });
  };

  const onChangeDiscount = (index: number, newDiscount: number | null) => {
    const invoiceProducts = [...extendedInvoiceDto.invoiceProducts];
    if (newDiscount === null) {
      invoiceProducts[index].discount = null;
    } else {
      invoiceProducts[index].discount = newDiscount;
    }
    const units = invoiceProducts[index].units !== null ? invoiceProducts[index].units! : 0;
    const discount = newDiscount || 0;
    const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
    const taxBase = (invoiceProducts[index].amount || 0) * units * (1 - discount / 100);
    const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
    const vatAmount = (taxBase * vatPercentage) / 100;
    invoiceProducts[index].subtotal = roundTwoDecimals(taxBase);
    invoiceProducts[index].vatAmount = roundTwoDecimals(vatAmount);
    invoiceProducts[index].total = roundTwoDecimals(taxBase + equivalenceSurchargeAmount + vatAmount);
    setExtendedInvoiceDto({ ...extendedInvoiceDto, invoiceProducts });
  };

  // PRODUCTOS MANUALES

  const removeManualProduct = (index: number) => {
    const manualInvoiceProducts = [...extendedInvoiceDto.manualInvoiceProducts];
    manualInvoiceProducts.splice(index, 1);
    setExtendedInvoiceDto({ ...extendedInvoiceDto, manualInvoiceProducts });
  };

  const onChangeManualUnits = (index: number, newUnits: number | null) => {
    const manualInvoiceProducts = [...extendedInvoiceDto.manualInvoiceProducts];
    if (newUnits === null) {
      manualInvoiceProducts[index].units = null;
      manualInvoiceProducts[index].hasErrorUnits = true;
    } else {
      manualInvoiceProducts[index].units = newUnits;
      manualInvoiceProducts[index].hasErrorUnits = false;
    }
    const amount = manualInvoiceProducts[index].amount !== null ? manualInvoiceProducts[index].amount! : 0;
    const discount = manualInvoiceProducts[index].discount !== null ? manualInvoiceProducts[index].discount! : 0;
    const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
    const taxBase = amount * (manualInvoiceProducts[index].units || 0) * (1 - discount / 100);
    const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
    const vatAmount = (taxBase * vatPercentage) / 100;
    manualInvoiceProducts[index].subtotal = roundTwoDecimals(taxBase);
    manualInvoiceProducts[index].vatAmount = roundTwoDecimals(vatAmount);
    manualInvoiceProducts[index].total = roundTwoDecimals(taxBase + equivalenceSurchargeAmount + vatAmount);
    setExtendedInvoiceDto({ ...extendedInvoiceDto, manualInvoiceProducts });
  };

  const onChangeManualAmount = (index: number, newAmount: number | null) => {
    const manualInvoiceProducts = [...extendedInvoiceDto.manualInvoiceProducts];
    if (newAmount === null) {
      manualInvoiceProducts[index].amount = null;
      manualInvoiceProducts[index].hasErrorAmount = true;
    } else {
      manualInvoiceProducts[index].amount = newAmount;
      if (extendedInvoiceDto.invoiceType === InvoiceType.Normal) {
        manualInvoiceProducts[index].hasErrorAmount = newAmount <= 0;
      } else {
        manualInvoiceProducts[index].hasErrorAmount = newAmount >= 0;
      }
    }
    const units = manualInvoiceProducts[index].units !== null ? manualInvoiceProducts[index].units! : 0;
    const discount = manualInvoiceProducts[index].discount !== null ? manualInvoiceProducts[index].discount! : 0;
    const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
    const taxBase = (manualInvoiceProducts[index].amount || 0) * units * (1 - discount / 100);
    const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
    const vatAmount = (taxBase * vatPercentage) / 100;
    manualInvoiceProducts[index].subtotal = roundTwoDecimals(taxBase);
    manualInvoiceProducts[index].vatAmount = roundTwoDecimals(vatAmount);
    manualInvoiceProducts[index].total = roundTwoDecimals(taxBase + equivalenceSurchargeAmount + vatAmount);
    setExtendedInvoiceDto({ ...extendedInvoiceDto, manualInvoiceProducts });
  };

  const onChangeManualDiscount = (index: number, newDiscount: number | null) => {
    const manualInvoiceProducts = [...extendedInvoiceDto.manualInvoiceProducts];
    if (newDiscount === null) {
      manualInvoiceProducts[index].discount = null;
    } else {
      manualInvoiceProducts[index].discount = newDiscount;
    }
    const units = manualInvoiceProducts[index].units !== null ? manualInvoiceProducts[index].units! : 0;
    const discount = newDiscount || 0;
    const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
    const taxBase = (manualInvoiceProducts[index].amount || 0) * units * (1 - discount / 100);
    const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
    const vatAmount = (taxBase * vatPercentage) / 100;
    manualInvoiceProducts[index].subtotal = roundTwoDecimals(taxBase);
    manualInvoiceProducts[index].vatAmount = roundTwoDecimals(vatAmount);
    manualInvoiceProducts[index].total = roundTwoDecimals(taxBase + equivalenceSurchargeAmount + vatAmount);
    setExtendedInvoiceDto({ ...extendedInvoiceDto, manualInvoiceProducts });
  };

  // NEW PRODUCT

  const removeNewProduct = () => {
    setNewInvoiceProductDto({
      productId: -1,
      name: '',
      units: null,
      amount: null,
      discountedAmount: 0,
      discount: null,
      vatAmount: 0,
      equivalenceSurcharge: 0,
      subtotal: 0,
      total: 0,
      product: null,
      hasErrorUnits: false,
      hasErrorAmount: false,
      hasErrorVatPercentage: false,
    });
    setQuery('');
  };

  const onChangeNewInvoiceProductUnits = (newUnits: number | null) => {
    const copyNewInvoiceProductDto = { ...newInvoiceProductDto };
    if (newUnits === null) {
      copyNewInvoiceProductDto.units = null;
      copyNewInvoiceProductDto.hasErrorUnits = true;
    } else {
      copyNewInvoiceProductDto.units = newUnits;
      copyNewInvoiceProductDto.hasErrorUnits = false;
    }
    const amount = copyNewInvoiceProductDto.amount !== null ? copyNewInvoiceProductDto.amount! : 0;
    const discount = copyNewInvoiceProductDto.discount !== null ? copyNewInvoiceProductDto.discount! : 0;
    const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
    const taxBase = amount * (newUnits || 0) * (1 - discount / 100);
    copyNewInvoiceProductDto.subtotal = roundTwoDecimals(taxBase);
    const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
    copyNewInvoiceProductDto.equivalenceSurcharge = roundTwoDecimals(equivalenceSurchargeAmount);
    const vatAmount = (taxBase * vatPercentage) / 100;
    copyNewInvoiceProductDto.vatAmount = roundTwoDecimals(vatAmount);
    copyNewInvoiceProductDto.total = roundTwoDecimals(taxBase + equivalenceSurchargeAmount + vatAmount);
    const discountedAmount = (copyNewInvoiceProductDto.amount || 0) * (1 - discount / 100);
    copyNewInvoiceProductDto.discountedAmount = roundTwoDecimals(discountedAmount);
    setNewInvoiceProductDto(copyNewInvoiceProductDto);
  };

  const onChangeNewInvoiceProductAmount = (newAmount: number | null) => {
    const copyNewInvoiceProductDto = { ...newInvoiceProductDto };
    if (newAmount === null) {
      copyNewInvoiceProductDto.amount = null;
      copyNewInvoiceProductDto.hasErrorAmount = true;
    } else {
      copyNewInvoiceProductDto.amount = newAmount;
      if (extendedInvoiceDto.invoiceType === InvoiceType.Normal) {
        copyNewInvoiceProductDto.hasErrorAmount = newAmount <= 0;
      } else {
        copyNewInvoiceProductDto.hasErrorAmount = newAmount >= 0;
      }
    }
    const amount = copyNewInvoiceProductDto.amount !== null ? copyNewInvoiceProductDto.amount! : 0;
    const discount = copyNewInvoiceProductDto.discount !== null ? copyNewInvoiceProductDto.discount! : 0;
    const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
    const taxBase = amount * (copyNewInvoiceProductDto.units || 0) * (1 - discount / 100);
    copyNewInvoiceProductDto.subtotal = roundTwoDecimals(taxBase);
    const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
    copyNewInvoiceProductDto.equivalenceSurcharge = roundTwoDecimals(equivalenceSurchargeAmount);
    const vatAmount = (taxBase * vatPercentage) / 100;
    copyNewInvoiceProductDto.vatAmount = roundTwoDecimals(vatAmount);
    copyNewInvoiceProductDto.total = roundTwoDecimals(taxBase + equivalenceSurchargeAmount + vatAmount);
    const discountedAmount = (copyNewInvoiceProductDto.amount || 0) * (1 - discount / 100);
    copyNewInvoiceProductDto.discountedAmount = roundTwoDecimals(discountedAmount);
    setNewInvoiceProductDto(copyNewInvoiceProductDto);
  };

  const onChangeNewInvoiceProductDiscount = (newDiscount: number | null) => {
    const copyNewInvoiceProductDto = { ...newInvoiceProductDto };
    if (newDiscount === null) {
      copyNewInvoiceProductDto.discount = null;
    } else {
      copyNewInvoiceProductDto.discount = newDiscount;
    }
    const amount = copyNewInvoiceProductDto.amount !== null ? copyNewInvoiceProductDto.amount! : 0;
    const discount = copyNewInvoiceProductDto.discount !== null ? copyNewInvoiceProductDto.discount! : 0;
    const vatPercentage = parseFloat(process.env.REACT_APP_IVA!);
    const taxBase = amount * (copyNewInvoiceProductDto.units || 0) * (1 - discount / 100);
    copyNewInvoiceProductDto.subtotal = roundTwoDecimals(taxBase);
    const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
    copyNewInvoiceProductDto.equivalenceSurcharge = roundTwoDecimals(equivalenceSurchargeAmount);
    const vatAmount = (taxBase * vatPercentage) / 100;
    copyNewInvoiceProductDto.vatAmount = roundTwoDecimals(vatAmount);
    copyNewInvoiceProductDto.total = roundTwoDecimals(taxBase + equivalenceSurchargeAmount + vatAmount);
    const discountedAmount = (copyNewInvoiceProductDto.amount || 0) * (1 - discount / 100);
    copyNewInvoiceProductDto.discountedAmount = roundTwoDecimals(discountedAmount);
    setNewInvoiceProductDto(copyNewInvoiceProductDto);
  };

  const addInvoiceProduct = () => {
    const copyExtendedInvoiceDto = { ...extendedInvoiceDto };
    if (newInvoiceProductDto.productId !== null && newInvoiceProductDto.productId > 0) {
      copyExtendedInvoiceDto.invoiceProducts.push({
        productId: newInvoiceProductDto.productId,
        units: newInvoiceProductDto.units,
        amount: newInvoiceProductDto.amount,
        discountedAmount: newInvoiceProductDto.discountedAmount,
        vatAmount: newInvoiceProductDto.vatAmount,
        equivalenceSurcharge: newInvoiceProductDto.equivalenceSurcharge,
        discount: newInvoiceProductDto.discount,
        subtotal: newInvoiceProductDto.subtotal,
        total: newInvoiceProductDto.total,
        product: newInvoiceProductDto.product,
        hasErrorUnits: newInvoiceProductDto.hasErrorUnits,
        hasErrorAmount: newInvoiceProductDto.hasErrorAmount,
        hasErrorVatPercentage: newInvoiceProductDto.hasErrorVatPercentage,
      });
    } else {
      copyExtendedInvoiceDto.manualInvoiceProducts.push({
        name: newInvoiceProductDto.name,
        units: newInvoiceProductDto.units,
        amount: newInvoiceProductDto.amount,
        vatAmount: newInvoiceProductDto.vatAmount,
        discountedAmount: newInvoiceProductDto.discountedAmount,
        equivalenceSurcharge: newInvoiceProductDto.equivalenceSurcharge,
        discount: newInvoiceProductDto.discount,
        subtotal: newInvoiceProductDto.subtotal,
        total: newInvoiceProductDto.total,
        hasErrorUnits: newInvoiceProductDto.hasErrorUnits,
        hasErrorAmount: newInvoiceProductDto.hasErrorAmount,
        hasErrorVatPercentage: newInvoiceProductDto.hasErrorVatPercentage,
      });
    }
    setExtendedInvoiceDto(copyExtendedInvoiceDto);
    setNewInvoiceProductDto({
      productId: -1,
      name: '',
      units: 1,
      amount: null,
      discountedAmount: 0,
      vatAmount: 0,
      equivalenceSurcharge: 0,
      discount: null,
      subtotal: 0,
      total: 0,
      product: null,
      hasErrorUnits: false,
      hasErrorAmount: false,
      hasErrorVatPercentage: false,
    });
    setQuery('');
  };

  const exportElectronicInvoice = async () => {
    if (requestingXml) {
      return;
    }
    setRequestingXml(true);
    try {
      myToastr.info('Obteniendo la factura electrónica. Espera por favor.');
      const result = await api.getInvoiceXml(invoice!.id);
      const url: string = window.URL.createObjectURL(new Blob([result]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factura-${invoice!.internalId}.xml`);
      document.body.appendChild(link);
      link.click();
    } catch (e) {
      myToastr.error('Hubo un error obteniendo el XML de la factura');
    } finally {
      setRequestingXml(false);
    }
  };

  const printInvoice = async () => {
    if (requestingPdf) {
      return;
    }
    setRequestingPdf(true);
    try {
      myToastr.info('Obteniendo la factura en formato PDF. Espera por favor.');
      const result = await api.getInvoicePdf(invoice!.id);
      const url: string = window.URL.createObjectURL(new Blob([result]));
      printJS(url);
    } catch (e) {
      myToastr.error('Hubo un error obteniendo la factura en formato PDF.');
    } finally {
      setRequestingPdf(false);
    }
  };

  const onCloseCustomerModal = (customer: Customer | null) => {
    setSelectedCustomer(null);
    setShowCustomerModal(false);
    if (!customer) {
      myToastr.error('Debes rellenar los datos obligatorios del cliente para poder crear la factura.');
      customerSelectRef?.current?.clearValue();
      return;
    }
    if (customer) {
      const copyExtendedInvoiceDto = { ...extendedInvoiceDto };
      copyExtendedInvoiceDto.customerId = customer.id;
      copyExtendedInvoiceDto.customer = customer;
      copyExtendedInvoiceDto.customerName = customer?.name ? customer.name : '';
      setExtendedInvoiceDto(copyExtendedInvoiceDto);
    }
  };

  const deleteInvoice = async () => {
    setRequesting(true);
    try {
      await api.deleteInvoice(invoice!.id);
      myToastr.success('Factura eliminada');
      navigate('/facturas');
      return;
    } catch (e) {
      myToastr.error('Error al eliminar la factura');
    }
    setRequesting(false);
  };

  const saveInvoice = async () => {
    if (extendedInvoiceDto.invoiceProducts.length === 0 && extendedInvoiceDto.manualInvoiceProducts.length === 0) {
      myToastr.error('Debes añadir al menos un producto');
      return;
    }
    const hasInvoiceProductsErrors: boolean = extendedInvoiceDto.invoiceProducts.some(
      (invoiceProduct: ExtendedInvoiceProductDto) => invoiceProduct.hasErrorUnits || invoiceProduct.hasErrorAmount || invoiceProduct.hasErrorVatPercentage,
    );
    if (hasInvoiceProductsErrors) {
      myToastr.error('Hay errores en los productos');
      return;
    }
    const hasManualInvoiceProductsErrors: boolean = extendedInvoiceDto.manualInvoiceProducts.some(
      (extendedManualInvoiceProductDto: ExtendedManualInvoiceProductDto) =>
        extendedManualInvoiceProductDto.hasErrorUnits || extendedManualInvoiceProductDto.hasErrorAmount || extendedManualInvoiceProductDto.hasErrorVatPercentage,
    );
    if (hasManualInvoiceProductsErrors) {
      myToastr.error('Hay errores en los productos');
      return;
    }
    try {
      if (invoice !== null) {
        const updateInvoiceDto: UpdateInvoiceDto = {
          observations: extendedInvoiceDto.observations,
          date: extendedInvoiceDto.date,
          invoiceStatus: extendedInvoiceDto.invoiceStatus,
          invoiceProducts: extendedInvoiceDto.invoiceProducts.map((extendedInvoiceProductDto: ExtendedInvoiceProductDto) => ({
            productId: extendedInvoiceProductDto.productId,
            units: extendedInvoiceProductDto.units!,
            amount: extendedInvoiceProductDto.amount!,
            discount: extendedInvoiceProductDto.discount!,
          })),
          manualInvoiceProducts: extendedInvoiceDto.manualInvoiceProducts.map((extendedManualInvoiceProductDto: ExtendedManualInvoiceProductDto) => ({
            name: extendedManualInvoiceProductDto.name,
            units: extendedManualInvoiceProductDto.units!,
            amount: extendedManualInvoiceProductDto.amount!,
            discount: extendedManualInvoiceProductDto.discount!,
          })),
          notes: extendedInvoiceDto.notes,
        };
        await api.updateInvoice(invoice.id, updateInvoiceDto);
        myToastr.success('Factura actualizada');
        navigate(0);
      } else {
        const createInvoiceDto: CreateInvoiceDto = {
          observations: extendedInvoiceDto.observations,
          date: extendedInvoiceDto.date,
          invoiceType: extendedInvoiceDto.invoiceType,
          invoiceStatus: extendedInvoiceDto.invoiceStatus,
          storeId: extendedInvoiceDto.storeId,
          customerId: organizationType === OrganizationType.B2C ? extendedInvoiceDto.customerId : null,
          organizationClientId: organizationType === OrganizationType.B2B ? extendedInvoiceDto.organizationClientId : null,
          invoiceProducts: extendedInvoiceDto.invoiceProducts.map((extendedInvoiceProductDto: ExtendedInvoiceProductDto) => ({
            productId: extendedInvoiceProductDto.productId,
            units: extendedInvoiceProductDto.units!,
            amount: extendedInvoiceProductDto.amount!,
            discount: extendedInvoiceProductDto.discount!,
          })),
          manualInvoiceProducts: extendedInvoiceDto.manualInvoiceProducts.map((extendedManualInvoiceProductDto: ExtendedManualInvoiceProductDto) => ({
            name: extendedManualInvoiceProductDto.name,
            units: extendedManualInvoiceProductDto.units!,
            amount: extendedManualInvoiceProductDto.amount!,
            discount: extendedManualInvoiceProductDto.discount!,
          })),
          notes: extendedInvoiceDto.notes,
        };
        const newInvoice: Invoice = await api.createInvoice(createInvoiceDto);
        myToastr.success('Factura creada');
        navigate(`/factura/${newInvoice.id}`);
      }
    } catch (e: any) {
      const httpExceptionDto: HttpExceptionDto = e.response.data;
      myToastr.error(httpExceptionDto.message);
    }
  };

  const postInvoice = async () => {
    if (extendedInvoiceDto.invoiceProducts.length === 0 && extendedInvoiceDto.manualInvoiceProducts.length === 0) {
      myToastr.error('Debes añadir al menos un producto');
      return;
    }
    const hasInvoiceProductsErrors: boolean = extendedInvoiceDto.invoiceProducts.some(
      (invoiceProduct: ExtendedInvoiceProductDto) => invoiceProduct.hasErrorUnits || invoiceProduct.hasErrorAmount || invoiceProduct.hasErrorVatPercentage,
    );
    if (hasInvoiceProductsErrors) {
      myToastr.error('Hay errores en los productos');
      return;
    }
    const hasManualInvoiceProductsErrors: boolean = extendedInvoiceDto.manualInvoiceProducts.some(
      (extendedManualInvoiceProductDto: ExtendedManualInvoiceProductDto) =>
        extendedManualInvoiceProductDto.hasErrorUnits || extendedManualInvoiceProductDto.hasErrorAmount || extendedManualInvoiceProductDto.hasErrorVatPercentage,
    );
    if (hasManualInvoiceProductsErrors) {
      myToastr.error('Hay errores en los productos');
      return;
    }
    try {
      if (invoice !== null) {
        const updateInvoiceDto: UpdateInvoiceDto = {
          observations: extendedInvoiceDto.observations,
          date: extendedInvoiceDto.date,
          invoiceStatus: InvoiceStatus.Posted,
          invoiceProducts: extendedInvoiceDto.invoiceProducts.map((extendedInvoiceProductDto: ExtendedInvoiceProductDto) => ({
            productId: extendedInvoiceProductDto.productId,
            units: extendedInvoiceProductDto.units!,
            amount: extendedInvoiceProductDto.amount!,
            discount: extendedInvoiceProductDto.discount!,
          })),
          manualInvoiceProducts: extendedInvoiceDto.manualInvoiceProducts.map((extendedManualInvoiceProductDto: ExtendedManualInvoiceProductDto) => ({
            name: extendedManualInvoiceProductDto.name,
            units: extendedManualInvoiceProductDto.units!,
            amount: extendedManualInvoiceProductDto.amount!,
            discount: extendedManualInvoiceProductDto.discount!,
          })),
          notes: extendedInvoiceDto.notes,
        };
        await api.updateInvoice(invoice.id, updateInvoiceDto);
        myToastr.success('Factura actualizada');
        navigate(0);
      } else {
        const createInvoiceDto: CreateInvoiceDto = {
          observations: extendedInvoiceDto.observations,
          date: extendedInvoiceDto.date,
          invoiceType: extendedInvoiceDto.invoiceType,
          invoiceStatus: InvoiceStatus.Posted,
          storeId: extendedInvoiceDto.storeId,
          customerId: organizationType === OrganizationType.B2C ? extendedInvoiceDto.customerId : null,
          organizationClientId: organizationType === OrganizationType.B2B ? extendedInvoiceDto.organizationClientId : null,
          invoiceProducts: extendedInvoiceDto.invoiceProducts.map((extendedInvoiceProductDto: ExtendedInvoiceProductDto) => ({
            productId: extendedInvoiceProductDto.productId,
            units: extendedInvoiceProductDto.units!,
            amount: extendedInvoiceProductDto.amount!,
            discount: extendedInvoiceProductDto.discount!,
            total: extendedInvoiceProductDto.total,
          })),
          manualInvoiceProducts: extendedInvoiceDto.manualInvoiceProducts.map((extendedManualInvoiceProductDto: ExtendedManualInvoiceProductDto) => ({
            name: extendedManualInvoiceProductDto.name,
            units: extendedManualInvoiceProductDto.units!,
            amount: extendedManualInvoiceProductDto.amount!,
            discount: extendedManualInvoiceProductDto.discount!,
            total: extendedManualInvoiceProductDto.total,
          })),
          notes: extendedInvoiceDto.notes,
        };
        const newInvoice: Invoice = await api.createInvoice(createInvoiceDto);
        myToastr.success('Factura creada');
        navigate(`/factura/${newInvoice.id}`);
      }
    } catch (e: any) {
      const httpExceptionDto: HttpExceptionDto = e.response.data;
      myToastr.error(httpExceptionDto.message);
    }
  };

  // NOTES

  const onCloseNoteModal = () => {
    setShowNotesModal(false);
  };

  const onSaveNoteModal = async (notes: string | null) => {
    try {
      const updatedInvoice: Invoice = await api.updateInvoiceNotes(invoice!.id, { notes: notes === null ? '' : notes });
      setInvoice(updatedInvoice);
      setExtendedInvoiceDto({ ...extendedInvoiceDto, notes: updatedInvoice.notes });
    } catch (e) {}
    setShowNotesModal(false);
  };

  const onDeleteNoteModal = async () => {
    try {
      const updatedInvoice: Invoice = await api.updateInvoiceNotes(invoice!.id, { notes: '' });
      setInvoice(updatedInvoice);
      setExtendedInvoiceDto({ ...extendedInvoiceDto, notes: updatedInvoice.notes });
    } catch (e) {}
    setShowNotesModal(false);
  };

  const onCustomerChange = (customer: Customer | null) => {
    if (!customer) {
      setExtendedInvoiceDto({
        ...extendedInvoiceDto,
        customerId: -1,
        customer: null,
      });
      return;
    }
    if (!customer.billingBusinessName) {
      myToastr.info('El cliente seleccionado no tiene Razón social, por favor, añádelo');
      setSelectedCustomer(customer);
      setShowCustomerModal(true);
      return;
    }
    if (!customer.cif) {
      myToastr.info('El cliente seleccionado no tiene CIF/NIF, por favor, añádelo');
      setSelectedCustomer(customer);
      setShowCustomerModal(true);
      return;
    }
    if (!customer.billingAddress1) {
      myToastr.info('El cliente seleccionado no tiene dirección de facturación, por favor, añádela');
      setSelectedCustomer(customer);
      setShowCustomerModal(true);
      return;
    }
    if (!customer.billingPostalCode) {
      myToastr.info('El cliente seleccionado no tiene código postal de facturación, por favor, añádelo');
      setSelectedCustomer(customer);
      setShowCustomerModal(true);
      return;
    }
    if (!customer.billingCity) {
      myToastr.info('El cliente seleccionado no tiene ciudad de facturación, por favor, añádela');
      setSelectedCustomer(customer);
      setShowCustomerModal(true);
      return;
    }
    if (!customer.billingProvince) {
      myToastr.info('El cliente seleccionado no tiene provincia de facturación, por favor, añádela');
      setSelectedCustomer(customer);
      setShowCustomerModal(true);
      return;
    }
    if (!customer.billingCountry) {
      myToastr.info('El cliente seleccionado no tiene país de facturación, por favor, añádelo');
      setSelectedCustomer(customer);
      setShowCustomerModal(true);
      return;
    }
    setExtendedInvoiceDto({
      ...extendedInvoiceDto,
      customerId: customer.id,
      customer,
    });
  };

  const onExpirationModalClose = (expiration: Expiration | null) => {
    if (expiration) {
      setInvoice({ ...invoice!, expiration });
    }
    setShowExpirationModal(false);
  };

  const onDeleteExpiration = async (result: boolean) => {
    if (result) {
      try {
        await api.deleteExpiration(invoice!.expiration!.id);
        myToastr.success('Vencimiento eliminado correctamente');
        await getInvoice();
      } catch (e: any) {
        myToastr.error(e.response.data.message);
      }
    }
    setShowDeleteExpirationConfirmModal(false);
  };

  if (selectedStoreId <= 0) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center">
        <h1>Selecciona una tienda</h1>
      </div>
    );
  }

  return (
    <div className="invoice-view">
      <div className="d-flex flex-row align-items-center invoice-view-header gap-2">
        <Link to="/facturas">
          <ChevronLeft className="me-1" size={16} />
          Volver
        </Link>
        <h1 className="flex-grow-1">
          {invoice !== null ? `Factura #${invoice.internalId}` : `Crear factura ${extendedInvoiceDto.invoiceType === InvoiceType.Normal ? '' : 'rectificativa / abono'.trim()}`}
        </h1>
        {showNewCorrectiveInvoice && (
          <Link to={`/factura/${invoice?.id}/rectificativa`} className="d-flex align-items-center create-button invoiceType-rectification" style={{ textDecoration: 'none', color: 'white' }}>
            <Plus className="me-1" size={14} /> Nueva Rectificativa
          </Link>
        )}
        {(user.role === Role.SuperAdmin || user.role === Role.Admin || user.role === Role.Manager) &&
          invoice?.invoiceType === InvoiceType.Normal &&
          invoice?.invoiceStatus === InvoiceStatus.Posted && (
            <>
              <button className="create-button create-expiration me-2" onClick={() => setShowExpirationModal(true)}>
                {invoice.expiration ? 'Actualizar' : 'Crear'} Vencimiento
              </button>
              {invoice.expiration && (
                <button
                  className="create-button invoiceType-rectification me-2"
                  onClick={() => {
                    const someExpirationPaymentInRemittance = invoice.expiration?.expirationPayments.some((e) => Boolean(e.remittanceId));
                    if (someExpirationPaymentInRemittance) {
                      myToastr.error('No se puede eliminar el vencimiento porque tiene pagos asociados a una remesa.');
                      return;
                    }
                    setShowDeleteExpirationConfirmModal(true);
                  }}
                >
                  Eliminar Vencimiento
                </button>
              )}
            </>
          )}
        {extendedInvoiceDto.notes !== null && extendedInvoiceDto.notes !== '' ? (
          <div className="d-flex flex-row align-items-center clickable has-notes" onClick={() => setShowNotesModal(true)}>
            <Plus className="me-1" size={14} />
            <span>1 Nota</span>
          </div>
        ) : (
          <div className="d-flex flex-row align-items-center clickable" onClick={() => setShowNotesModal(true)}>
            <File className="me-1" size={16} />
            <span>Añadir nota</span>
          </div>
        )}
        {extendedInvoiceDto.invoiceStatus === InvoiceStatus.Posted && (
          <React.Fragment>
            {/* <div className="d-flex flex-row align-items-center cursor-pointer mx-3" onClick={exportElectronicInvoice}>
              <Download className="me-1" size={16} />
              <span>Exportar fac. electrónica</span>
            </div> */}
            <div className="d-flex flex-row align-items-center cursor-pointer" onClick={printInvoice}>
              <Printer className="me-1" size={16} />
              <span>Imprimir</span>
            </div>
          </React.Fragment>
        )}
      </div>
      <div className="row">
        <div className="col-8">
          <div className="white-container">
            {Array.isArray(invoice?.rectifyingInvoices) && invoice!.rectifyingInvoices!.length > 0 && (
              <div className="row mb-4">
                <div className="col">
                  <div className="d-flex flex-row gap-2">
                    <label>{invoice!.rectifyingInvoices!.length > 1 ? 'Facturas rectificativas' : 'Factura rectificativa'}:</label>
                    {invoice!.rectifyingInvoices!.map((rectifyingInvoice) => (
                      <Link
                        key={rectifyingInvoice.id}
                        to={
                          rectifyingInvoice.invoiceStatus === InvoiceStatus.Posted
                            ? `/factura/${rectifyingInvoice.id}`
                            : `/factura/${rectifyingInvoice.parentInvoiceId}/rectificativa/${rectifyingInvoice.id}`
                        }
                        style={{
                          fontSize: 14,
                          textDecoration: 'none',
                        }}
                      >
                        <strong>#{rectifyingInvoice.internalId}</strong>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {invoice?.parentInvoice && (
              <div className="row mb-4">
                <div className="col">
                  <div className="d-flex flex-row gap-2">
                    <label>Factura origen:</label>
                    <Link
                      to={`/factura/${invoice?.parentInvoiceId}`}
                      style={{
                        fontSize: 14,
                        textDecoration: 'none',
                      }}
                    >
                      <strong>#{invoice?.parentInvoice?.internalId}</strong>
                    </Link>
                  </div>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col-8">
                <div className="d-flex flex-column">
                  <label>Cliente:</label>
                  {organizationType === OrganizationType.B2C && (
                    <>
                      {invoice?.customer ? (
                        <CustomerInfo customer={invoice.customer} hideChangeCustomerButton onAssignCustomer={onCustomerChange} />
                      ) : invoice?.organizationClient ? (
                        <strong className="text-sm">
                          {invoice.organizationClient.relatedOrganization.businessName} (CIF: {invoice.organizationClient.relatedOrganization.cif})
                        </strong>
                      ) : extendedInvoiceDto.customer ? (
                        <CustomerInfo onRemoveCustomer={() => onCustomerChange(null)} customer={extendedInvoiceDto.customer} onAssignCustomer={onCustomerChange} balance={null} />
                      ) : (
                        <CustomersFinder customer={extendedInvoiceDto.customer} onClose={onCustomerChange} billingDataRequired={true} parentRef={customerSelectRef} />
                      )}
                    </>
                  )}
                  {organizationType === OrganizationType.B2B && (
                    <select
                      className="form-select"
                      disabled={extendedInvoiceDto.invoiceStatus === InvoiceStatus.Posted || (extendedInvoiceDto.organizationClientId !== null && extendedInvoiceDto.organizationClientId > 0)}
                      onChange={(e) => {
                        const organizationClientId = parseInt(e.target.value);
                        setExtendedInvoiceDto({
                          ...extendedInvoiceDto,
                          organizationClientId: organizationClientId,
                        });
                      }}
                      value={extendedInvoiceDto.organizationClientId || ''}
                    >
                      <option value={-1} disabled>
                        Selecciona un cliente
                      </option>
                      {organizationClients.map((organizationClient) => (
                        <option key={organizationClient.id} value={organizationClient.id}>
                          {organizationClient.relatedOrganization.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="col-4">
                <div className="d-flex flex-column">
                  <label>Fecha:</label>
                  <input
                    type="date"
                    value={extendedInvoiceDto.date || ''}
                    onChange={(e) => setExtendedInvoiceDto({ ...extendedInvoiceDto, date: e.target.value })}
                    disabled={extendedInvoiceDto.invoiceStatus === InvoiceStatus.Posted}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-4">
          <div className="d-flex flex-column gap-1">
            <div className="white-container d-flex flex-column">
              <label>Tipo:</label>
              <div className={clsx('d-flex align-items-center invoice-status', `invoiceType-${extendedInvoiceDto.invoiceType}`)}>
                {extendedInvoiceDto.invoiceType === InvoiceType.Rectification ? 'Rectificativa' : 'Normal'}
              </div>
            </div>
            <div className="white-container d-flex flex-column">
              <label>Estado:</label>
              <div className={clsx('d-flex align-items-center invoice-status', extendedInvoiceDto.invoiceStatus)}>
                {extendedInvoiceDto.invoiceStatus === InvoiceStatus.Posted ? 'Contabilizada' : 'Pendiente'}
              </div>
            </div>
          </div>
        </div>
      </div>
      {(extendedInvoiceDto.customerId === null || extendedInvoiceDto.customerId <= 0) && (extendedInvoiceDto.organizationClientId === null || extendedInvoiceDto.organizationClientId <= 0) ? null : (
        <>
          <div className="white-container invoice-product-lines">
            <table>
              <thead>
                <tr>
                  <th className="delete text-center"></th>
                  <th className="">Concepto</th>
                  <th className="units text-center">Unidades</th>
                  <th className="amount text-center">
                    <div className="d-flex flex-column">
                      <span>Precio sin descuento €</span>
                      {extendedInvoiceDto.invoiceType === InvoiceType.Rectification && <span style={{ fontSize: 10 }}>Valor en negativo</span>}
                    </div>
                  </th>
                  <th className="discount-product text-center">Desc. %</th>
                  <th className="amount text-center">Precio con descuento €</th>
                  <th className="amount text-center">Subtotal €</th>
                  <th className="vatAmount text-center">IVA €</th>
                  {EQUIVALENCE_SURCHARGE > 0 && <th className="equivalenceSurcharge text-center">Recargo de equi. €</th>}
                  <th className="total text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {extendedInvoiceDto.invoiceStatus === InvoiceStatus.Posted ? (
                  <React.Fragment>
                    {extendedInvoiceDto.invoiceProducts.map((extendedInvoiceProductDto: ExtendedInvoiceProductDto, index: number) => {
                      const discountedAmount = (extendedInvoiceProductDto.amount || 0) * (1 - (extendedInvoiceProductDto.discount ? extendedInvoiceProductDto.discount / 100 : 0));
                      return (
                        <tr key={index}>
                          <td></td>
                          <td className="text-left">
                            <div className="d-flex flex-column">
                              <Link to={`/producto/${extendedInvoiceProductDto.product!.id}`} style={{ textDecoration: 'none' }}>
                                {extendedInvoiceProductDto.product!.name}
                              </Link>
                              <span style={{ fontSize: 11 }}>{extendedInvoiceProductDto.product!.sku}</span>
                            </div>
                          </td>
                          <td className="text-center">{extendedInvoiceProductDto.units}</td>
                          <td className="text-center">{formatNumber(extendedInvoiceProductDto.amount)}€</td>
                          <td className="text-center">{formatNumber(extendedInvoiceProductDto.discount)}%</td>
                          <td className="text-center">{formatNumber(discountedAmount)}€</td>
                          <td className="text-center">{formatNumber(extendedInvoiceProductDto.subtotal)}€</td>
                          <td className="text-center">{formatNumber(extendedInvoiceProductDto.vatAmount)}€</td>
                          {EQUIVALENCE_SURCHARGE > 0 && <td className="text-center">{formatNumber(extendedInvoiceProductDto.equivalenceSurcharge)}€</td>}
                          <td className="text-end">{formatNumber(extendedInvoiceProductDto.total)}€</td>
                        </tr>
                      );
                    })}
                    {extendedInvoiceDto.manualInvoiceProducts.map((extendedManualInvoiceProductDto: ExtendedManualInvoiceProductDto, index: number) => {
                      const discountedAmount = (extendedManualInvoiceProductDto.amount || 0) * (1 - (extendedManualInvoiceProductDto.discount ? extendedManualInvoiceProductDto.discount / 100 : 0));
                      return (
                        <tr key={index}>
                          <td></td>
                          <td className="text-left">{extendedManualInvoiceProductDto.name}</td>
                          <td className="text-center">{extendedManualInvoiceProductDto.units}</td>
                          <td className="text-center">{formatNumber(extendedManualInvoiceProductDto.amount)}€</td>
                          <td className="text-center">{formatNumber(extendedManualInvoiceProductDto.discount)}%</td>
                          <td className="text-center">{formatNumber(discountedAmount)}€</td>
                          <td className="text-center">{formatNumber(extendedManualInvoiceProductDto.subtotal)}€</td>
                          <td className="text-center">{formatNumber(extendedManualInvoiceProductDto.vatAmount)}€</td>
                          {EQUIVALENCE_SURCHARGE > 0 && <td className="text-center">{formatNumber(extendedManualInvoiceProductDto.equivalenceSurcharge)}€</td>}
                          <td className="text-end">{formatNumber(extendedManualInvoiceProductDto.total)}€</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {extendedInvoiceDto.invoiceProducts.map((extendedInvoiceProductDto: ExtendedInvoiceProductDto, index: number) => {
                      return (
                        <tr key={index}>
                          <td>
                            <div title="Eliminar línea">
                              <X className="cursor-pointer" onClick={() => removeProduct(index)} size={12} color="#808A95" />
                            </div>
                          </td>
                          <td>{extendedInvoiceProductDto.product!.name}</td>
                          <td>
                            <input
                              type="number"
                              className={clsx({ 'is-invalid': extendedInvoiceProductDto.hasErrorUnits })}
                              value={extendedInvoiceProductDto.units || ''}
                              min={1}
                              placeholder="Uds."
                              onChange={(e: any) => {
                                let value = e.target.value;
                                if (e.target.value) {
                                  value = parseInt(e.target.value);
                                } else {
                                  value = null;
                                }
                                onChangeUnits(index, value);
                              }}
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="number"
                              pattern="[0-9]+([\.,][0-9]+)?"
                              className={clsx({ 'is-invalid': extendedInvoiceProductDto.hasErrorAmount })}
                              value={extendedInvoiceProductDto.amount || ''}
                              min={0}
                              placeholder="Precio €"
                              onChange={(e: any) => {
                                let value = e.target.value;
                                if (e.target.value) {
                                  value = parseFloat(e.target.value);
                                } else {
                                  value = null;
                                }
                                onChangeAmount(index, value);
                              }}
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="number"
                              pattern="[0-9]+([\.,][0-9]+)?"
                              value={extendedInvoiceProductDto.discount || ''}
                              min={0}
                              max={100}
                              placeholder="Desc. %"
                              onChange={(e: any) => {
                                let value = e.target.value;
                                if (e.target.value) {
                                  value = Math.min(100, parseFloat(e.target.value));
                                } else {
                                  value = null;
                                }
                                onChangeDiscount(index, value);
                              }}
                            />
                          </td>
                          <td className="text-center">{formatNumber(extendedInvoiceProductDto.discountedAmount)}€</td>
                          <td className="text-center">{formatNumber(extendedInvoiceProductDto.subtotal)}€</td>
                          <td className="text-center">{formatNumber(extendedInvoiceProductDto.vatAmount)}€</td>
                          {EQUIVALENCE_SURCHARGE > 0 && <td className="text-center">{formatNumber(extendedInvoiceProductDto.equivalenceSurcharge)}€</td>}
                          <td className="text-center">{formatNumber(extendedInvoiceProductDto.total)}€</td>
                        </tr>
                      );
                    })}
                    {extendedInvoiceDto.manualInvoiceProducts.map((extendedManualInvoiceProductDto: ExtendedManualInvoiceProductDto, index: number) => {
                      return (
                        <tr key={index}>
                          <td>
                            <div title="Eliminar línea manual">
                              <X className="cursor-pointer" onClick={() => removeManualProduct(index)} size={12} color="#808A95" />
                            </div>
                          </td>
                          <td>{extendedManualInvoiceProductDto.name}</td>
                          <td className="text-center">
                            <input
                              type="number"
                              className={clsx({ 'is-invalid': extendedManualInvoiceProductDto.hasErrorUnits })}
                              value={extendedManualInvoiceProductDto.units || ''}
                              min={1}
                              placeholder="Uds."
                              onChange={(e: any) => {
                                let value = e.target.value;
                                if (e.target.value) {
                                  value = parseInt(e.target.value);
                                } else {
                                  value = null;
                                }
                                onChangeManualUnits(index, value);
                              }}
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="number"
                              pattern="[0-9]+([\.,][0-9]+)?"
                              className={clsx({ 'is-invalid': extendedManualInvoiceProductDto.hasErrorAmount })}
                              value={extendedManualInvoiceProductDto.amount || ''}
                              min={0}
                              placeholder="Precio €"
                              onChange={(e: any) => {
                                let value = e.target.value;
                                if (e.target.value) {
                                  value = parseFloat(e.target.value);
                                } else {
                                  value = null;
                                }
                                onChangeManualAmount(index, value);
                              }}
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="number"
                              pattern="[0-9]+([\.,][0-9]+)?"
                              value={extendedManualInvoiceProductDto.discount || ''}
                              min={0}
                              max={100}
                              placeholder="Desc. %"
                              onChange={(e: any) => {
                                let value = e.target.value;
                                if (e.target.value) {
                                  value = Math.min(100, parseFloat(e.target.value));
                                } else {
                                  value = null;
                                }
                                onChangeManualDiscount(index, value);
                              }}
                            />
                          </td>
                          <td className="text-center">{formatNumber(extendedManualInvoiceProductDto.discountedAmount)}€</td>
                          <td className="text-center">{formatNumber(extendedManualInvoiceProductDto.vatAmount)}€</td>
                          {EQUIVALENCE_SURCHARGE > 0 && <td className="text-center">{formatNumber(extendedManualInvoiceProductDto.equivalenceSurcharge)}€</td>}
                          <td className="text-center">{formatNumber(extendedManualInvoiceProductDto.total)}€</td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td className="">
                        <div title="Eliminar línea">
                          <X className="cursor-pointer" onClick={removeNewProduct} size={12} color="#808A95" />
                        </div>
                      </td>
                      <td className="">
                        <Autocomplete
                          inputProps={{
                            placeholder: 'Buscar producto o añadir línea manual',
                            style: {
                              textAlign: 'left',
                              width: '100%',
                            },
                          }}
                          wrapperStyle={{ position: 'relative', display: 'inline-block', width: '100%' }}
                          value={newInvoiceProductDto?.product?.name || newInvoiceProductDto?.name || query || ''}
                          items={stock}
                          getItemValue={(item: Stock) => item.product.name}
                          onSelect={(_: string, item: Stock) => {
                            let amount = 0;
                            if (organizationType === OrganizationType.B2C) {
                              // Eliminar el iva al precio de venta al público
                              amount = roundTwoDecimals(item.pvp / (1 + parseFloat(process.env.REACT_APP_IVA) / 100));
                            } else if (organizationType === OrganizationType.B2B) {
                              amount = item.retailPrice;
                            }
                            if (extendedInvoiceDto.invoiceType === InvoiceType.Rectification) {
                              amount *= -1;
                            }
                            const vatAmount = (amount * parseFloat(process.env.REACT_APP_IVA)) / 100;
                            const equivalenceSurcharge = (amount * EQUIVALENCE_SURCHARGE) / 100;
                            setNewInvoiceProductDto({
                              ...newInvoiceProductDto,
                              product: item.product,
                              productId: item.product.id,
                              amount,
                              discountedAmount: amount,
                              vatAmount: roundTwoDecimals(vatAmount),
                              equivalenceSurcharge: roundTwoDecimals(equivalenceSurcharge),
                            });
                          }}
                          onChange={(_, value: string) => {
                            setQuery(value);
                            setNewInvoiceProductDto({
                              ...newInvoiceProductDto,
                              name: value,
                            });
                          }}
                          renderMenu={(children) => {
                            return <div className="menu menu-items">{children}</div>;
                          }}
                          renderItem={(item: Stock, isHighlighted: boolean) => (
                            <div className={clsx('item d-flex flex-row', { 'item-highlighted': isHighlighted })} key={item.id}>
                              <span>{item.store.name}</span>
                              <span className="mx-2">-</span>
                              <span>{item.product.sku}</span>
                              <span className="mx-2">-</span>
                              <span>{item.product.name}</span>
                            </div>
                          )}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="number"
                          className={clsx({ 'is-invalid': newInvoiceProductDto.hasErrorUnits })}
                          value={newInvoiceProductDto.units || ''}
                          min={1}
                          placeholder="Uds."
                          onChange={(e: any) => {
                            let value = e.target.value;
                            if (e.target.value) {
                              value = parseInt(e.target.value);
                            } else {
                              value = null;
                            }
                            onChangeNewInvoiceProductUnits(value);
                          }}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="number"
                          pattern="[0-9]+([\.,][0-9]+)?"
                          className={clsx({ 'is-invalid': newInvoiceProductDto.hasErrorAmount })}
                          value={newInvoiceProductDto.amount || ''}
                          placeholder="Precio €"
                          onChange={(e: any) => {
                            let value = e.target.value;
                            if (e.target.value) {
                              value = parseFloat(e.target.value);
                            } else {
                              value = null;
                            }
                            onChangeNewInvoiceProductAmount(value);
                          }}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="number"
                          pattern="[0-9]+([\.,][0-9]+)?"
                          value={newInvoiceProductDto.discount || ''}
                          min={0}
                          max={100}
                          placeholder="Desc. %"
                          onChange={(e: any) => {
                            let value = e.target.value;
                            if (e.target.value) {
                              value = Math.min(100, parseFloat(e.target.value));
                            } else {
                              value = null;
                            }
                            onChangeNewInvoiceProductDiscount(value);
                          }}
                        />
                      </td>
                      <td className="text-center">{formatNumber(newInvoiceProductDto.discountedAmount)}€</td>
                      <td className="text-center">{formatNumber(newInvoiceProductDto.subtotal)}€</td>
                      <td className="text-center">{formatNumber(newInvoiceProductDto.vatAmount)}€</td>
                      {EQUIVALENCE_SURCHARGE > 0 && <td className="text-center">{formatNumber(newInvoiceProductDto.equivalenceSurcharge)}€</td>}
                      <td className="text-center">
                        <button className="filled" onClick={addInvoiceProduct} disabled={isAddProductDisabled}>
                          Añadir
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end">
            <div className="invoice-totals">
              <table>
                <thead>
                  <tr>
                    <th>Uds. total</th>
                    <th>Base imponible</th>
                    <th>IVA total</th>
                    {EQUIVALENCE_SURCHARGE > 0 && <th>Recargo de equiv.</th>}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <input type="text" readOnly disabled value={invoiceTotals.units} />
                    </td>
                    <td>
                      <input type="text" readOnly disabled value={`${formatNumber(invoiceTotals.taxBase)}€`} />
                    </td>
                    <td>
                      <input type="text" readOnly disabled value={`${formatNumber(invoiceTotals.vat)}€`} />
                    </td>
                    {EQUIVALENCE_SURCHARGE > 0 && (
                      <td>
                        <input type="text" readOnly disabled value={`${formatNumber(invoiceTotals.equivalenceSurcharge)}€`} />
                      </td>
                    )}
                    <td>
                      <input type="text" readOnly disabled value={`${formatNumber(invoiceTotals.total)}€`} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="white-container">
            <div className="d-flex flex-column">
              <label>Observaciones:</label>
              <textarea
                value={extendedInvoiceDto.observations || ''}
                onChange={(e: any) =>
                  setExtendedInvoiceDto({
                    ...extendedInvoiceDto,
                    observations: e.target.value,
                  })
                }
                disabled={extendedInvoiceDto.invoiceStatus === InvoiceStatus.Posted}
              />
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <div>
              {invoice !== null && extendedInvoiceDto.invoiceStatus === InvoiceStatus.Pending && (
                <button className="outlined" onClick={() => setShowDeleteInvoiceConfirmModal(true)}>
                  Eliminar
                </button>
              )}
            </div>
            <div>
              {(invoice === null || extendedInvoiceDto.invoiceStatus === InvoiceStatus.Pending) && (
                <button className={clsx('me-3 outlined')} onClick={postInvoice} disabled={requesting}>
                  Contabilizar
                </button>
              )}
              {(invoice === null || extendedInvoiceDto.invoiceStatus === InvoiceStatus.Pending) && (
                <button className={clsx('filled', { disabled: disableButton })} onClick={saveInvoice} disabled={requesting}>
                  Guardar
                </button>
              )}
            </div>
          </div>
        </>
      )}
      <CustomerModal show={showCustomerModal} closeModal={onCloseCustomerModal} customer={selectedCustomer} billingDataRequired={true} />
      {invoice?.invoiceStatus === InvoiceStatus.Pending && (
        <ConfirmModal
          acceptButtonClass="accept-button"
          show={showDeleteInvoiceConfirmModal}
          title="Eliminar Factura"
          content={`¿Estás seguro que quieres eliminar la factura #${invoice.internalId}?`}
          closeModal={(result: boolean) => {
            setShowDeleteInvoiceConfirmModal(false);
            if (result) {
              deleteInvoice();
            }
          }}
        />
      )}
      <NoteModal editable={true} show={showNotesModal} notes={extendedInvoiceDto.notes} closeModal={onCloseNoteModal} saveNotes={onSaveNoteModal} deleteNotes={onDeleteNoteModal} />
      {invoice && <ExpirationModal closeModal={onExpirationModalClose} expiration={invoice?.expiration || null} invoice={invoice} show={showExpirationModal} />}
      <ConfirmModal
        acceptButtonClass="accept-button"
        closeModal={onDeleteExpiration}
        content="¿Estás seguro que quieres eliminar el vencimiento?"
        show={showDeleteExpirationConfirmModal}
        title="Eliminar Vencimiento"
      />
    </div>
  );
};

export default InvoiceView;
